import { useTranslation } from 'react-i18next'
import { DEPARTURE_AT } from '@/lib/journey'
import { Stamp } from '@/components/ui/Stamp'
import { DepartureBoard } from '@/components/home/DepartureBoard'
import { BoardingPass } from '@/components/home/BoardingPass'

const ROSE_TICKS = 32

/** Rosa dei venti di sfondo: anelli, tacche e stella a otto punte, solo tratto. */
function CompassRose({ className }: { className?: string }) {
  return (
    <svg viewBox="-100 -100 200 200" aria-hidden="true" className={className}>
      <g fill="none" stroke="currentColor" strokeWidth="0.6">
        <circle r="98" />
        <circle r="84" strokeDasharray="1 3" />
        <circle r="40" />
        {Array.from({ length: ROSE_TICKS }, (_, i) => {
          const a = (i / ROSE_TICKS) * 2 * Math.PI
          const inner = i % 4 === 0 ? 86 : 92
          return <line key={i} x1={inner * Math.cos(a)} y1={inner * Math.sin(a)} x2={98 * Math.cos(a)} y2={98 * Math.sin(a)} />
        })}
        <path d="M0-80L9-9L80 0L9 9L0 80L-9 9L-80 0L-9-9Z" />
        <path d="M0-50L5-5L50 0L5 5L0 50L-5 5L-50 0L-5-5Z" transform="rotate(45)" />
      </g>
    </svg>
  )
}

/** Sei bicchieri a triangolo visti dall'alto. */
function CupsIcon() {
  return (
    <svg viewBox="0 0 32 28" className="size-8 sm:size-10 short:size-6" fill="none" stroke="currentColor" strokeWidth="1.8">
      {[[6, 6], [16, 6], [26, 6], [11, 15], [21, 15], [16, 24]].map(([cx, cy]) => <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="4" />)}
    </svg>
  )
}

function WheelIcon() {
  return (
    <svg viewBox="-16 -16 32 32" className="size-8 sm:size-10 short:size-6" fill="none" stroke="currentColor" strokeWidth="1.8">
      <circle r="13" />
      <path d="M0-13V13M-13 0H13M-9.2-9.2L9.2 9.2M9.2-9.2L-9.2 9.2" strokeWidth="1.2" />
      <circle r="3" fill="currentColor" />
    </svg>
  )
}

/** La home è una sola schermata: titolo, tabellone delle partenze e due carte d'imbarco. */
export function Home() {
  const { t, i18n } = useTranslation()
  const departureDate = new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'long', timeZone: 'Europe/Rome' }).format(DEPARTURE_AT)
  const departureTime = new Intl.DateTimeFormat(i18n.language, { hour: 'numeric', minute: '2-digit', timeZone: 'Europe/Rome' }).format(DEPARTURE_AT)
  return (
    <main id="main" className="relative isolate flex min-h-svh flex-col overflow-hidden px-4 pt-20 pb-6 sm:px-6 md:pt-24 lg:px-8 short:pt-16 short:pb-3">
      <div aria-hidden="true" className="chart-grid absolute inset-0 -z-20 opacity-40 [mask-image:radial-gradient(ellipse_at_70%_60%,black,transparent_70%)]" />
      <CompassRose className="absolute -right-[35vw] -bottom-[20vw] -z-10 w-[max(26rem,90vw)] text-line motion-safe:animate-[spin_180s_linear_infinite] lg:-right-40 lg:-bottom-56 lg:w-[46rem]" />

      <section aria-labelledby="hero-heading" className="mx-auto flex w-full max-w-6xl flex-1 flex-col justify-center gap-[clamp(1rem,3.5svh,2.5rem)] lg:grid lg:grid-cols-[1.1fr_1fr] lg:items-center lg:gap-16 short:grid short:grid-cols-[1fr_1.1fr] short:items-center short:gap-8">
        <div>
          <Stamp>{t('home.hero.eyebrow', { date: departureDate, time: departureTime })}</Stamp>
          <h1 id="hero-heading" className="mt-[clamp(0.75rem,2.5svh,1.75rem)] font-display font-medium tracking-tight text-ink" style={{ fontVariationSettings: '"SOFT" 60, "opsz" 144' }}>
            <span className="block text-[clamp(2.75rem,min(14vw,10svh),8rem)] leading-[0.92]">{t('home.hero.title1')}</span>
            <span className="mt-2 block text-[clamp(1.25rem,min(6.2vw,4.5svh),3rem)] leading-tight italic text-sea-ink">{t('home.hero.title2')}</span>
          </h1>
        </div>

        <div className="flex flex-col gap-[clamp(1rem,3.5svh,2rem)] short:gap-3">
          <DepartureBoard />
          <nav aria-label={t('home.tickets.aria')} className="grid grid-cols-2 gap-3 sm:gap-5">
            <BoardingPass to="stoccoPong" tone="coral" icon={<CupsIcon />} gate={t('home.tickets.stoccoPong.gate')} title={t('home.tickets.stoccoPong.title')} text={t('home.tickets.stoccoPong.text')} />
            <BoardingPass to="mimiWheel" tone="sea" icon={<WheelIcon />} gate={t('home.tickets.mimiWheel.gate')} title={t('home.tickets.mimiWheel.title')} text={t('home.tickets.mimiWheel.text')} />
          </nav>
        </div>
      </section>
    </main>
  )
}
